<template>
  <div class="p-2 flex items-center font-body text-sm leading-tight gap-2">
    <div
      v-if="column.cell?.checkbox && typeof row[column.cell.checkbox] == 'boolean'"
      class="flex-none"
      data-testid="l-table-cell__checkbox"
    >
      <l-checkbox
        v-model="checkboxState"
        small
        @click.stop="emit('checkbox', checkboxState, rowIndex, toRaw(column))"
      />
    </div>
    <div
      v-if="column.cell?.radio && typeof row[column.cell.radio] == 'boolean'"
      class="flex-none"
      data-testid="l-table-cell__radio"
    >
      <l-radio
        v-model="radioState"
        small
        @toggle="emit('radio', radioState, rowIndex, toRaw(column))"
      />
    </div>
    <div
      v-if="column.cell?.switch && typeof row[column.cell.switch] == 'boolean'"
      class="flex-none"
      data-testid="l-table-cell__switch"
    >
      <l-switch v-model="switchState" :disabled="disableSwitch" @click.stop="toggleSwitch" />
    </div>
    <div
      v-if="column.cell?.status && validStatus"
      class="flex-none"
      data-testid="l-table-cell__status"
    >
      <l-status size="small" :status="row[column.cell.status]" />
    </div>
    <div
      v-if="column.cell?.avatar?.text && row[column.cell.avatar.text]"
      data-testid="l-table-cell__avatar"
      class="flex-none"
    >
      <l-avatar
        :style="{ backgroundColor: avatarColor, color: avatarTextColor }"
        size="small"
        :value="row[column.cell?.avatar?.text]"
      />
    </div>
    <div
      v-if="
        column.cell?.spot?.visible &&
        (typeof column.cell?.spot?.visible !== 'boolean' ? row[column.cell.spot.visible] : true)
      "
      data-testid="l-table-cell__spot"
      class="flex-none"
    >
      <div class="w-2 h-2 rounded-full" :style="{ backgroundColor: spotColor }" />
    </div>
    <div
      v-if="column.cell?.iconLeft && row[column.cell.iconLeft]"
      data-testid="l-table-cell__icon-left"
      class="flex-none"
    >
      <l-icon class="align-center" size="small" :name="row[column.cell?.iconLeft]" />
    </div>
    <div
      v-if="
        (column.key && typeof row[column.key] !== 'undefined') ||
        (column.cell?.secondLineKey && row[column.cell.secondLineKey]) ||
        column.cell?.ignoreKey ||
        mergedColumns.length > 0
      "
      data-testid="l-table-cell__text"
      class="flex flex-col min-w-0 overflow-hidden break-words"
      :class="[column.cell?.ignoreKey ? 'flex-1' : 'flex-shrink']"
    >
      <div
        v-if="(column.key && typeof row[column.key] !== 'undefined') || column.cell?.ignoreKey"
        :class="[
          column.cell?.secondLineKey && row[column.cell.secondLineKey]
            ? 'font-bold line-clamp-1'
            : 'line-clamp-2'
        ]"
      >
        <slot>{{ column.cell?.ignoreKey ? row[column.key] : row[column.key] || '' }}</slot>
        <template v-for="mc in mergedColumns.filter(m => m.mergeAs === 'inline')" :key="mc.key">
          <span class="text-n-500">
            · {{ mc.mergeLabel ? `${mc.mergeLabel}: ` : '' }}{{ mc.value }}</span
          >
        </template>
      </div>
      <div
        v-if="column.cell?.secondLineKey && row[column.cell.secondLineKey]"
        class="text-xs"
        :class="[column.key && row[column.key] ? 'line-clamp-1' : 'line-clamp-2']"
      >
        {{ row[column.cell.secondLineKey] }}
      </div>
      <template v-for="mc in mergedColumns.filter(m => m.mergeAs === 'secondLine')" :key="mc.key">
        <div class="text-xs text-n-500 line-clamp-1">
          {{ mc.mergeLabel ? `${mc.mergeLabel}: ` : '' }}{{ mc.value }}
        </div>
      </template>
      <div v-if="mergedColumns.some(m => m.mergeAs === 'badge')" class="flex flex-wrap gap-1 mt-1">
        <template v-for="mc in mergedColumns.filter(m => m.mergeAs === 'badge')" :key="mc.key">
          <span
            class="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-n-100 dark:bg-n-600 text-n-600 dark:text-n-200"
          >
            {{ mc.mergeLabel ? `${mc.mergeLabel}: ` : '' }}{{ mc.value }}
          </span>
        </template>
      </div>
    </div>
    <div
      v-if="column.cell?.helper && row[column.cell.helper]"
      class="flex-none"
      data-testid="l-table-cell__helper"
    >
      <l-tooltip trigger="mouseenter focus" placement="bottom">
        <l-icon class="align-center text-b-500" size="small" name="question-circle" />
        <template #content>{{ row[column.cell.helper] }}</template>
      </l-tooltip>
    </div>
    <div
      v-if="column.cell?.iconRight && row[column.cell.iconRight]"
      data-testid="l-table-cell__icon-right"
      class="flex-none"
    >
      <l-icon class="align-center" size="small" :name="row[column.cell?.iconRight]" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, toRaw, watch } from 'vue'

import LAvatar from '../LAvatar.vue'
import LCheckbox from '../LCheckbox.vue'
import LIcon from '../LIcon.vue'
import LRadio from '../LRadio.vue'
import LStatus from '../LStatus.vue'
import LSwitch from '../LSwitch.vue'
import LTooltip from '../LTooltip.vue'
import type { MergedColumnData, TableCellColumnDefinition, TableDataColumn } from './types'

type Props = {
  column: TableDataColumn
  rowIndex: number
  row: Record<string, any>
  /** Enforces the cell switch component to be disabled */
  disableSwitch?: boolean
  /** Data from merged columns to render */
  mergedColumns?: MergedColumnData[]
}

const props = withDefaults(defineProps<Props>(), {
  disableSwitch: false,
  mergedColumns: () => []
})

const emit = defineEmits<{
  /** Emitted when the checkbox state of the cell changes, the payload of the event is `(value: boolean, rowIndex: number, column: TableDataColumn)` */
  checkbox: [value: boolean, rowIndex: number, column: TableDataColumn]
  /** Emitted when the radio state of the cell changes, the payload of the event is `(value: boolean, rowIndex: number, column: TableDataColumn)` */
  radio: [value: boolean, rowIndex: number, column: TableDataColumn]
  /** Emitted when the switch state of the cell changes, the payload of the event is `(value: boolean, rowIndex: number, column: TableDataColumn)` */
  switch: [value: boolean, rowIndex: number, column: TableDataColumn]
}>()

defineSlots<{
  /** This is the default slot, used to override the text part of the component */
  default: object
}>()

watch(
  () => props.row,
  () => {
    refreshDynamicStates()
  },
  { deep: true }
)

const checkboxState = ref<boolean>(calculateBooleanControlState('checkbox'))
const radioState = ref<boolean>(calculateBooleanControlState('radio'))
const switchState = ref<boolean>(calculateBooleanControlState('switch'))

function toggleSwitch(): void {
  if (props.disableSwitch) return
  switchState.value = !switchState.value
  emit('switch', switchState.value, props.rowIndex, toRaw(props.column))
}

defineExpose({ toggleSwitch })

function calculateBooleanControlState(controlName: 'checkbox' | 'radio' | 'switch'): boolean {
  if (!props.column?.cell) return false
  const currentKey = props.column.cell[
    controlName as keyof TableCellColumnDefinition
  ] as keyof TableCellColumnDefinition
  if (controlName === 'switch' && props.disableSwitch) {
    return false
  }
  return typeof props.row[currentKey] == 'boolean' ? props.row[currentKey] || false : false
}

function refreshDynamicStates(): void {
  checkboxState.value = calculateBooleanControlState('checkbox')
  radioState.value = calculateBooleanControlState('radio')
  switchState.value = calculateBooleanControlState('switch')
}

const validStatus = computed(() => {
  const validStatuses = ['active', 'pending', 'error', 'inactive']

  return props.column?.cell?.status && validStatuses.includes(props.row[props.column.cell.status])
})

const avatarColor = computed(() => {
  return parseHexColor(props.row[props.column.cell?.avatar?.color || ''] || '', '#2a2a42')
})

const avatarTextColor = computed(() => {
  return parseHexColor(props.row[props.column.cell?.avatar?.textColor || ''] || '', '#ffffff')
})

const spotColor = computed(() => {
  return parseHexColor(props.row[props.column.cell?.spot?.color || ''] || '', '#9C6DFE')
})

function parseHexColor(color: string, fallback = '#9C6DFE'): string {
  if (color !== '') {
    const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i
    if (hex.test(color)) {
      return color
    }
  }

  return fallback
}
</script>
